<script lang="ts">
  import type { PlayerInterface, VideoThumbnail } from '@foundation-player/loader';
  import { getContext } from 'svelte';
  import { derived } from 'svelte/store';
  import Slider from '../../shared/components/Slider.svelte';
  import type { FocusItem, FocusLayer } from '../../shared/focus-manager/focus-types';
  import type { PlayerStateContext } from '../../shared/types/player-state';
  import type { SliderState } from '../../shared/types/slider';
  import { formatTime, getTimeLabel } from '../../shared/utils/format-time';
  import type { MediaModeOptions } from '../types/internal/media-mode';
  import Thumbnail from './Thumbnail.svelte';

  export let player: PlayerInterface | undefined = undefined;
  export let focusLayer: FocusLayer;
  export let focusName: FocusItem;

  const {
    linearAdPlaying,
    adDuration,
    durationInSeconds: contentDuration,
    currentTime: contentTime,
    currentAdTime,
    liveMedium,
  } = getContext<PlayerStateContext>('playerState');
  const { hasPrimeControls } = getContext<MediaModeOptions>('mediaModeOptions');

  const currentTime = derived(
    [linearAdPlaying, currentAdTime, contentTime],
    ([linearAdPlaying, currentAdTime, contentTime]) => (linearAdPlaying ? currentAdTime : contentTime),
  );
  const duration = derived(
    [linearAdPlaying, adDuration, contentDuration],
    ([linearAdPlaying, adDuration, contentDuration]) => (linearAdPlaying ? adDuration : contentDuration),
  );

  let sliderPositionInSeconds: number;
  let sliderPositionInPixels: number;
  let sliderWidthInPixels: number;
  let sliding = false;
  let hovering = false;

  let videoThumbnail: VideoThumbnail | undefined = undefined;

  $: formattedTime = formatTime(sliding ? sliderPositionInSeconds : $currentTime);
  $: formattedDuration = formatTime($duration);

  const startSliding = (event: CustomEvent<SliderState>) => {
    sliding = true;
    sliderPositionInSeconds = event.detail.value;
  };

  const stopSliding = (event: CustomEvent<SliderState>) => {
    if (sliding) {
      sliding = false;
      // We have to unset the video thumbnail. Otherwise, when the user starts
      // seeking again, the old thumbnail might be shown for a short time until
      // the new one is loaded.
      videoThumbnail = undefined;
      player?.seek(event.detail.value);
    }
  };

  const onSliderChange = async (event: CustomEvent<SliderState>) => {
    if (sliding && player) {
      sliderPositionInSeconds = event.detail.value;
      sliderPositionInPixels = event.detail.px;
      sliderWidthInPixels = event.detail.size;

      videoThumbnail = await player.getThumbnail(sliderPositionInSeconds);
    }
  };

  const onSliderHovering = async (event: CustomEvent<SliderState>) => {
    // We show the thumbnail on hover or while sliding. We don't want to do both
    // at the same time.
    if (sliding) {
      return;
    }

    hovering = true;
    sliderPositionInPixels = event.detail.px;
    sliderWidthInPixels = event.detail.size;

    if (player) {
      videoThumbnail = await player.getThumbnail(event.detail.value);
    }
  };

  const onStopHovering = () => {
    hovering = false;
  };

  const getValueText = (current: number, _min: number, max: number) => {
    const hasReasonableMaxValue = Number.isFinite(max) && max > 0;

    return getTimeLabel(current) + (hasReasonableMaxValue ? ` von ${getTimeLabel(max)}` : '');
  };

  let thumbnailPosition = 0;
  let thumbnailWidth = 320;

  $: {
    const maxThumbnailWidth = videoThumbnail ? videoThumbnail.coords.w : 320;
    // limit thumbnail width in relation to available size (but not larger than
    // the image size). The factor of 0.4 was chosen based on what looks fine.
    thumbnailWidth = Math.min(sliderWidthInPixels * 0.4, maxThumbnailWidth);

    const maxThumbnailPosition = sliderWidthInPixels - thumbnailWidth;
    const idealThumbnailPosition = sliderPositionInPixels - thumbnailWidth / 2;

    thumbnailPosition = Math.min(maxThumbnailPosition, Math.max(0, idealThumbnailPosition));
  }
</script>

<div class="seekbar">
  {#if $liveMedium && !$linearAdPlaying}
    <span class="live-badge progress-info" class:has-prime-controls={hasPrimeControls}>Live</span>
  {:else}
    <span class="progress-info" data-testid="time" class:has-prime-controls={hasPrimeControls}>
      {formattedTime}
      {#if $duration > 0}
        / {formattedDuration}{/if}
    </span>
  {/if}

  {#if videoThumbnail && (sliding || hovering)}
    <div class="thumbnail" style:left="{thumbnailPosition}px">
      <Thumbnail image={videoThumbnail} width={thumbnailWidth} />
    </div>
  {/if}

  <div class={$$props.class} class:seekbar-wrapper={true} class:advertisement={$linearAdPlaying}>
    <Slider
      min={0}
      max={$duration}
      step={1}
      multiStep={10}
      current={sliding ? sliderPositionInSeconds : $currentTime}
      readonly={$linearAdPlaying || $duration === 0}
      presentationalOnly={$liveMedium}
      advertisement={$linearAdPlaying}
      on:startSliding={startSliding}
      on:stopSliding={stopSliding}
      on:change={onSliderChange}
      on:startHovering={onSliderHovering}
      on:hovering={onSliderHovering}
      on:stopHovering={onStopHovering}
      label="Schieberegler für Spielzeit"
      {getValueText}
      {focusLayer}
      {focusName}
    />
  </div>
</div>

<style module>
  .seekbar {
    position: relative;
  }

  .seekbar-wrapper {
    display: flex;
  }

  .seekbar-wrapper.advertisement {
    color: #f1bf42; /* Fallback for older browsers */
    --slider-track-color: var(--advertisement-color);
    --slider-hover-track-color: var(--advertisement-color);
  }

  .progress-info {
    line-height: 1;

    font-size: clamp(0.75em, 1cqw + 0.5em, 1em); /* Fallback for older browsers is the initial value of 1em */

    /* Make all digits use the same width, even for non-monospace fonts. This
     avoids jumping of the displayed time. */
    font-variant-numeric: tabular-nums;
  }

  .progress-info.has-prime-controls {
    /*
     * Move the time display out of the flow
     * Now the prime-controls get more space
     * So they appear more in the middle of the controls layer
     */
    position: absolute;
    bottom: 100%;
  }

  .live-badge {
    text-transform: uppercase;
    font-weight: 700;
    display: inline-flex;
    gap: 0.4em;
    align-items: center;
  }

  .live-badge::before {
    content: '';
    display: inline-block;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background-color: currentColor;
  }

  .thumbnail {
    position: absolute;
    bottom: 3em;
    background-color: #000000;
    outline: 1px solid #ffffff;
  }
</style>
